/**
 * WebSocket connection: hello handshake, auto-reconnect with backoff, ping
 * measurement, and the mandatory dev latency/loss simulator.
 *
 * The simulator delays messages in both directions and can drop `input` and
 * `snapshot` messages (the two loss-tolerant message kinds — WebSocket is TCP,
 * so this emulates what a lossy transport would do to the netcode).
 */

import type { ClientMessage, ServerMessage } from '@microligue/shared';
import { PROTOCOL_VERSION } from '@microligue/shared';

export interface NetSimSettings {
  delayMs: number;
  jitterMs: number;
  lossPct: number;
}

const SIM_STORAGE_KEY = 'microligue.netsim.v1';

export function loadNetSim(): NetSimSettings {
  try {
    const raw = localStorage.getItem(SIM_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<NetSimSettings>;
      return {
        delayMs: clampNum(parsed.delayMs, 0, 400),
        jitterMs: clampNum(parsed.jitterMs, 0, 100),
        lossPct: clampNum(parsed.lossPct, 0, 20),
      };
    }
  } catch {
    // corrupted storage — defaults
  }
  return { delayMs: 0, jitterMs: 0, lossPct: 0 };
}

function clampNum(v: unknown, min: number, max: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.min(Math.max(v, min), max) : min;
}

/** Ping samples kept for the windowed-minimum RTT (see basePingMs). */
const PING_WINDOW_MS = 30_000;

/**
 * RTT « plancher » : minimum des mesures d'une fenêtre glissante. Un gel du
 * thread principal retarde la réception du pong et GONFLE la mesure — jamais
 * l'inverse : le minimum fenêtré reste donc le vrai RTT du chemin même quand
 * le client saccade, tout en suivant une vraie dégradation réseau en une
 * fenêtre. C'est la valeur à donner au servo de cadence.
 */
export class RttFloor {
  private samples: { at: number; rtt: number }[] = [];

  constructor(private readonly windowMs = PING_WINDOW_MS) {}

  add(rtt: number, at: number): void {
    this.samples.push({ at, rtt });
    while (this.samples.length > 0 && at - (this.samples[0]?.at ?? 0) > this.windowMs) {
      this.samples.shift();
    }
  }

  /** Minimum de la fenêtre ; `fallback` tant qu'aucune mesure n'existe. */
  floor(fallback: number): number {
    if (this.samples.length === 0) return fallback;
    let min = Infinity;
    for (const s of this.samples) if (s.rtt < min) min = s.rtt;
    return min;
  }
}

export class Connection {
  pingMs = 0;
  connected = false;
  /** True once a connection has succeeded at least once (drives the UX). */
  everConnected = false;
  private readonly rttFloor = new RttFloor();

  onMessage: (msg: ServerMessage) => void = () => undefined;
  onStatus: (connected: boolean) => void = () => undefined;
  /** Called when the server evicted this session (a newer tab took over). */
  onEvicted: () => void = () => undefined;

  private ws: WebSocket | null = null;
  private closedByUser = false;
  private backoffMs = 500;
  private pingTimer: number | null = null;
  private reconnectTimer: number | null = null;

  constructor(
    /** Shared latency-simulator settings (owned by the app, edited by the panel). */
    readonly sim: NetSimSettings,
  ) {}

  connect(): void {
    this.closedByUser = false;
    this.open();
  }

  private open(): void {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/ws`);
    this.ws = ws;

    ws.addEventListener('open', () => {
      this.backoffMs = 500;
      this.connected = true;
      this.everConnected = true;
      // Identity is the signed session cookie the browser sends with the
      // handshake — hello only negotiates the protocol version.
      this.sendNow({ type: 'hello', protocol: PROTOCOL_VERSION });
      this.onStatus(true);
      this.startPing();
    });

    ws.addEventListener('message', (ev) => {
      const raw = typeof ev.data === 'string' ? ev.data : '';
      let msg: ServerMessage;
      try {
        msg = JSON.parse(raw) as ServerMessage;
      } catch {
        return;
      }
      this.deliverIn(msg);
    });

    const onGone = (evt: Event): void => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.connected = false;
      this.stopPing();
      this.onStatus(false);
      // The server evicts a previous session with 4001 when a newer tab of the
      // same account connects. Auto-reconnecting would evict that newer tab in
      // turn — an endless ping-pong. Stop instead and tell the app.
      if ((evt as CloseEvent).code === 4001) {
        this.closedByUser = true;
        this.onEvicted();
        return;
      }
      if (!this.closedByUser) {
        this.reconnectTimer = window.setTimeout(() => this.open(), this.backoffMs);
        this.backoffMs = Math.min(this.backoffMs * 2, 8000);
      }
    };
    ws.addEventListener('close', onGone);
    ws.addEventListener('error', onGone);
  }

  disconnect(): void {
    this.closedByUser = true;
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.stopPing();
    this.ws?.close();
    this.ws = null;
    this.connected = false;
  }

  saveSim(): void {
    localStorage.setItem(SIM_STORAGE_KEY, JSON.stringify(this.sim));
  }

  /** Sends through the latency/loss simulator. */
  send(msg: ClientMessage): void {
    if (this.sim.lossPct > 0 && msg.type === 'input' && Math.random() * 100 < this.sim.lossPct) {
      return;
    }
    const delay = this.simDelay();
    if (delay <= 0) {
      this.sendNow(msg);
    } else {
      const payload = JSON.stringify(msg);
      window.setTimeout(() => {
        if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(payload);
      }, delay);
    }
  }

  private sendNow(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  private deliverIn(msg: ServerMessage): void {
    if (this.sim.lossPct > 0 && msg.type === 'snapshot' && Math.random() * 100 < this.sim.lossPct) {
      return;
    }
    const delay = this.simDelay();
    if (delay <= 0) {
      this.dispatch(msg);
    } else {
      window.setTimeout(() => this.dispatch(msg), delay);
    }
  }

  private dispatch(msg: ServerMessage): void {
    if (msg.type === 'pong') {
      const now = performance.now();
      const rtt = now - msg.t;
      this.pingMs = this.pingMs === 0 ? rtt : this.pingMs * 0.8 + rtt * 0.2;
      this.rttFloor.add(rtt, now);
    }
    this.onMessage(msg);
  }

  /**
   * RTT plancher pour le servo de cadence (voir RttFloor) : baser la cible de
   * file sur l'EWMA `pingMs` — gonflée par les gels du client — faisait
   * grossir le backlog (rejeu plus long, extrapolation adverse plus grande)
   * exactement quand la machine était déjà en difficulté.
   */
  get basePingMs(): number {
    return this.rttFloor.floor(this.pingMs);
  }

  /** One-way artificial delay (half of the configured round-trip added). */
  private simDelay(): number {
    if (this.sim.delayMs <= 0 && this.sim.jitterMs <= 0) return 0;
    return this.sim.delayMs / 2 + Math.random() * this.sim.jitterMs;
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = window.setInterval(() => {
      this.send({ type: 'ping', t: performance.now() });
    }, 2000);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }
}
